import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './features/auth/context/AuthContext';

import ProtectedRoute from './features/auth/components/ProtectedRoute';
import LoginPage from './features/auth/pages/LoginPage';
import Layout from './components/layout/Layout';
import DashboardPage from './features/dashboard/pages/DashboardPage';
import ClientListingPage from './features/clients/pages/ClientListingPage';
import ProjectListingPage from './features/projects/pages/ProjectListingPage';
import ServiceListingPage from './features/services/pages/ServiceListingPage';
import ServiceTemplateListingPage from './features/services/pages/ServiceTemplateListingPage';
import PublicServiceListingPage from './features/services/pages/PublicServiceListingPage';
import { PaymentListingPage, ClientPaymentsPage } from './features/payments';
import LicenceListingPage from './features/licences';
import { InvoiceListingPage } from './features/invoices';
import NotificationListingPage from './features/notifications/pages/NotificationListingPage';
import { QuoteListingPage, QuotePackageListingPage } from './features/quotes';

// Component που ελέγχει αν ο χρήστης είναι ήδη συνδεδεμένος για το login route
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Αν είμαστε ήδη στο login page, μην κρύβουμε το component κατά τη διάρκεια του login
  // γιατί προκαλεί unmount/remount και χάνεται το error state
  const currentPath = window.location.pathname;
  const isOnLoginPage = currentPath.includes('/login');
  
  // Μόνο κατά την αρχική φόρτωση (όχι στο login page) να δείχνουμε loading
  if (loading && !isOnLoginPage) {
    return (
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return isAuthenticated ? <Navigate to="/" replace /> : children;
};

function AppContent() {
  return (
    <Routes>
      {/* Public Route - Login */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Public Route - Services (no auth required) */}
      <Route path="/public/services/:token" element={<PublicServiceListingPage />} />
      
      {/* Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="clients" element={<ClientListingPage />} />
        <Route path="projects" element={<ProjectListingPage />} />
        <Route path="quotes" element={<QuoteListingPage />} />
        <Route path="quote-packages" element={<QuotePackageListingPage />} />
        <Route path="services" element={<ServiceListingPage />} />
        <Route path="service-templates" element={<ServiceTemplateListingPage />} />
        <Route path="invoices" element={<InvoiceListingPage />} />
        <Route path="payments" element={<PaymentListingPage />} />
        <Route path="licences" element={<LicenceListingPage />} />
        <Route path="clients/:clientId/payments" element={<ClientPaymentsPage />} />
        <Route path="notifications" element={<NotificationListingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;